import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const faqCategories = [
  {
    category: "Getting Started",
    questions: [
      {
        question: "How do I get started with Better Closings?",
        answer: "Getting started is simple. Book a demo call with our team, and we'll walk you through the process. Once you're ready, we'll set up your account and provide access to our secure portal for submitting closed loans. The entire onboarding process typically takes less than an hour."
      },
      {
        question: "Is there a setup fee or minimum commitment?",
        answer: "No, there are no setup fees, monthly minimums, or long-term contracts. You simply pay $249 per closed loan that we service. You can start or stop at any time with no penalties."
      },
      {
        question: "How do I submit closed loans to Better Closings?",
        answer: "We provide a secure online portal where you can submit loan details in about 30 seconds. Simply enter the borrower's contact information, closing date, and any branding preferences. For higher-volume partners, we also offer CRM integrations for automatic submissions."
      }
    ]
  },
  {
    category: "The Service",
    questions: [
      {
        question: "When does outreach happen after closing?",
        answer: "Our team begins outreach within 24-48 hours of receiving the closed loan submission. The initial contact includes a personalized email introduction followed by a phone call. The full 30-day follow-up sequence includes gift coordination, review requests, and referral conversations."
      },
      {
        question: "How do the gifts work?",
        answer: "We coordinate thoughtful gifts on your behalf. After initial contact, we present the client with a curated selection of gift options to choose from. Once they select, we handle ordering and delivery. The cost of gifts is included in your $249 fee—you set your preferred budget during onboarding."
      },
      {
        question: "Which review platforms do you support?",
        answer: "We can request reviews on virtually any platform your clients use, including Google Business Profile, Zillow, Realtor.com, Facebook, and more. During onboarding, you'll specify which platforms are most important to your business, and we'll prioritize accordingly."
      },
      {
        question: "How are referrals collected and delivered?",
        answer: "When a client expresses interest in referring someone, our team captures all relevant details including the referral's name, contact information, and context about their potential needs. This information is delivered to you via email within 24 hours, and we can also log it directly into your CRM if integrated."
      }
    ]
  },
  {
    category: "Compliance & Branding",
    questions: [
      {
        question: "Is Better Closings compliant with industry regulations?",
        answer: "Yes, our service is designed with compliance in mind. All communications avoid making guarantees or promises about specific outcomes. We use language like 'designed to increase' rather than 'guaranteed results.' Gift values are kept within appropriate limits, and all outreach is conducted professionally."
      },
      {
        question: "Will clients know they're speaking with a third party?",
        answer: "Our concierge team introduces themselves as working with your company, maintaining your brand presence throughout the experience. Clients perceive the service as an extension of your team, not a separate entity. All communications are branded to you."
      },
      {
        question: "Can I customize the messaging and approach?",
        answer: "Absolutely. During onboarding, you can specify your preferred tone, messaging guidelines, and any specific things you want us to mention or avoid. We'll work with you to ensure the client experience aligns perfectly with your brand."
      }
    ]
  },
  {
    category: "Pricing & Billing",
    questions: [
      {
        question: "When do I pay for the service?",
        answer: "We invoice monthly for all loans serviced in the previous month. Payment is due upon receipt of invoice. We accept all major credit cards, ACH transfers, and checks."
      },
      {
        question: "What if a client doesn't respond to outreach?",
        answer: "Our fee applies regardless of client engagement—we perform the work either way. That said, our proven sequences are designed to maximize response rates, and the majority of clients do engage with our outreach."
      },
      {
        question: "Are there volume discounts available?",
        answer: "Yes, we offer volume discounts for partners who consistently submit 25+ loans per month. Contact us to discuss pricing options for your specific volume."
      }
    ]
  },
  {
    category: "Support & Reporting",
    questions: [
      {
        question: "What kind of reporting do I receive?",
        answer: "You receive detailed monthly reports showing all activity for each loan, including outreach attempts, gifts sent, reviews collected, and referrals captured. We also provide aggregated analytics so you can track trends over time."
      },
      {
        question: "How do I contact support if I have questions?",
        answer: "Every partner has access to our dedicated support team via email, phone, and chat during business hours. We typically respond to inquiries within a few hours and are committed to providing exceptional service to our partners."
      },
      {
        question: "What's the turnaround time for starting service on a new loan?",
        answer: "Once you submit a closed loan through our portal, our team begins outreach within 24-48 hours. The full 30-day follow-up sequence runs automatically from there."
      }
    ]
  }
];

export const FAQPage = () => {
  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#0F0F0F]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="h-[2px] w-16 bg-[#C9A227] mx-auto mb-8" />
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Support</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Frequently Asked <span className="text-[#E5D5A5]">Questions</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              Find answers to common questions about Better Closings, our services, 
              pricing, and how we can help elevate your post-close experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section data-testid="faq-sections" className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
              className="mb-16 last:mb-0"
              data-testid={`faq-category-${category.category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <h2 className="font-serif text-2xl text-[#E5D5A5] mb-6 pb-4 border-b border-white/10">
                {category.category}
              </h2>
              
              <Accordion type="single" collapsible className="space-y-4">
                {category.questions.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`${categoryIndex}-${index}`}
                    className="bg-[#1A1A1A] border border-white/5 px-6 data-[state=open]:border-[#C9A227]/30 transition-colors"
                    data-testid={`faq-item-${categoryIndex}-${index}`}
                  >
                    <AccordionTrigger className="text-white hover:text-[#C9A227] text-left py-5 font-medium">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-400 leading-relaxed pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-24 bg-[#141414] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
              Still Have <span className="text-[#E5D5A5]">Questions?</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              Our team is here to help. Book a demo call and we'll answer all your 
              questions in person.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="faq-cta-primary">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-10 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  Contact Us
                </Button>
              </Link>
              <Link to="/contact" data-testid="faq-cta-secondary">
                <Button variant="outline" className="bg-transparent border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 rounded-none px-10 py-6 font-semibold text-sm tracking-wider uppercase transition-all duration-300">
                  Book a Demo
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
